import { lazy } from 'react';
import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import type { PermissionRequirement } from '@/interfaces/permission.interface';
import { PermissionGuard, SessionGuard } from './PermissionGuard';
import NotFoundPage from '../modules/Common/pages/NotFoundPage/NotFoundPage';
import { ChunkLoadErrorBoundary } from './ChunkLoadErrorBoundary';

const SidebarLayout = lazy(() => import('../layouts/SidebarLayout/SidebarLayout'));
const DashboardPage = lazy(() => import('../modules/Dashboard/pages/dashboardPage/DashboardPage'));

//! Clients
const ClientPage = lazy(() => import('../modules/Clients/pages/ClientsPage/ClientsPage'));
const InformationClientPage = lazy(
	() => import('../modules/Clients/pages/InformationClientPage/InformationClientPage'),
);
const TabsNavigationClientLayout = lazy(
	() =>
		import('../modules/Clients/layouts/TabsNavigationClientLayout/TabsNavigationClientLayout'),
);
const FormationClientPage = lazy(
	() => import('../modules/Clients/pages/FormationClientPage/FormationClientPage'),
);
const DefibrillatorClientPage = lazy(
	() => import('../modules/Clients/pages/DefibrillatorClientPage/DefibrillatorClientPage'),
);
const BudgetsClientPage = lazy(
	() => import('../modules/Clients/pages/BudgetsClientPage/BudgetsClientPage'),
);
const InvoicesClientPage = lazy(
	() => import('../modules/Clients/pages/InvoiceClientPage/InvoiceClientPage'),
);
const FollowUpClientPage = lazy(
	() => import('../modules/Clients/pages/FollowUpClientPage/FollowUpClientPage'),
);

// ! Suppliers
const SuppliersPage = lazy(() => import('../modules/Suppliers/pages/SuppliersPage/SuppliersPage'));
const SuppliersInformationPage = lazy(
	() => import('../modules/Suppliers/pages/InformationSuppliersPage/InformationSuppliersPage'),
);

const TabsNavigationSuppliersLayout = lazy(
	() =>
		import('../modules/Suppliers/layouts/TabsNavigationSuppliersLayout/TabsNavigationSuppliersLayout'),
);

const ProductsSuppliersPage = lazy(
	() => import('../modules/Suppliers/pages/ProductsSuppliersPage/ProductsSuppliersPage'),
);

const InvoicesSuppliersPage = lazy(
	() => import('../modules/Suppliers/pages/InvoiceSuppliersPage/InvoiceSuppliersPage'),
);

const FollowUpSuppliersPage = lazy(
	() => import('../modules/Suppliers/pages/FollowUpSuppliersPage/FollowUpSuppliersPage'),
);

// ! Subcontractors

const TabsNavigationSubcontractorsLayout = lazy(
	() =>
		import('../modules/Subcontractors/layouts/TabsNavigationSubcontractorsLayout/TabsNavigationSubcontractorsLayout'),
);

const SubcontractorsPage = lazy(
	() => import('../modules/Subcontractors/pages/SubcontractorsPage/SubcontractorsPage'),
);

const SubcontractorsInformationPage = lazy(
	() =>
		import('../modules/Subcontractors/pages/InformationSubcontractorsPage/InformationSubcontractorsPage'),
);

const SubcontractorsClientsPage = lazy(
	() =>
		import('../modules/Subcontractors/pages/ClientsSubcontractorsPage/ClientsSubcontractorsPage'),
);

const SubcontractorsCoursesPage = lazy(
	() =>
		import('../modules/Subcontractors/pages/CoursesSubcontractorsPage/CoursesSubcontractorsPage'),
);

const SubcontractorsInvoicesPage = lazy(
	() =>
		import('../modules/Subcontractors/pages/InvoicesSubcontractorsPage/InvoicesSubcontractorsPage'),
);

const SubcontractorsBudgetsPage = lazy(
	() =>
		import('../modules/Subcontractors/pages/BudgetsSubcontractorsPage/BudgetsSubcontractorsPage'),
);

const SubcontractorsFollowUpPage = lazy(
	() =>
		import('../modules/Subcontractors/pages/FollowUpSubcontractorsPage/FollowUpSubcontractorsPage'),
);

// ! Products and Services

// ! Defibrillators

const TabsNavigationDefibrillatorsLayout = lazy(
	() =>
		import('../modules/Defibrillators/layouts/TabsNavigationDefibrilatorsLayout/TabsNavigationDefibrilatorsLayout'),
);
const DefibrillatorPage = lazy(
	() => import('../modules/Defibrillators/pages/DefibrillatorsPage/DefibrillatorsPage'),
);

const InformationDefibrillatorPage = lazy(
	() =>
		import('../modules/Defibrillators/pages/InformationDefibrillatorsPage/InformationDefibrillatorsPage'),
);

const ReviewDefibrillatorsPage = lazy(
	() =>
		import('../modules/Defibrillators/pages/ReviewDefibrillatorsPage/ReviewDefibrillatorsPage'),
);

const NotesDefibrillatorsPage = lazy(
	() => import('../modules/Defibrillators/pages/NotesDefibrillatorsPage/NotesDefibrillatorsPage'),
);

const FollowUpDefibrillatorsPage = lazy(
	() =>
		import('../modules/Defibrillators/pages/FollowUpDefibrillatorsPage/FollowUpDefibrillatorsPage'),
);

const ProductsPage = lazy(() => import('../modules/Products/pages/ProductsPage/ProductsPage'));

// ! Kits

const KitsPage = lazy(() => import('../modules/Kits/pages/KitsPage'));

const InformationKitsPage = lazy(() => import('../modules/Kits/pages/InformationPage'));

const TabsNavigationKitsLayout = lazy(() => import('../modules/Kits/layout/TabsNavigationLayout'));

const HistoryPage = lazy(() => import('../modules/Kits/pages/HistoryPage'));

// ! Trainers

const TrainersPage = lazy(() => import('../modules/Trainers/pages/TrainersPage/TrainersPage'));

const TabsNavigationTrainersLayout = lazy(
	() => import('../modules/Trainers/layouts/TabsNavigationLayout/TabsNavigationLayout'),
);

const InformationTrainersPage = lazy(
	() => import('../modules/Trainers/pages/InformationPage/InformationPage'),
);

const NoteTrainersPage = lazy(() => import('../modules/Trainers/pages/NotesPage/NotesPage'));

const KitsTrainersPage = lazy(() => import('../modules/Trainers/pages/KitsPage/KitsPage'));

const ProvincesTrainersPage = lazy(
	() => import('../modules/Trainers/pages/ProvincesPage/ProvincesPage'),
);

const FilesTrainersPage = lazy(() => import('../modules/Trainers/pages/FilesPage/FilesPage'));

const FollowUpTrainersPage = lazy(
	() => import('../modules/Trainers/pages/FollowUpPage/FollowUpPage'),
);

const CoursesTrainersPage = lazy(() => import('../modules/Trainers/pages/CoursesPage/CoursesPage'));

// ! Formations Types

const FormationTypePage = lazy(
	() => import('../modules/FormationTypes/pages/FormationTypes/FormationTypes'),
);

const FormationTypeDetailsPage = lazy(
	() => import('../modules/FormationTypes/pages/FormationTypeDetails/FormationTypeDetails'),
);

const FormationTypeCommunity = lazy(
	() => import('../modules/FormationTypes/pages/FormationTypeCommunity/FormationTypeCommunity'),
);

// ! Formations
const FormationPage = lazy(
	() => import('../modules/Formations/pages/FormationsPage/FormationsPage'),
);

const TabsNavigationFormationsLayout = lazy(
	() => import('../modules/Formations/layouts/TabsNavigation/TabsNavigation.layout'),
);

const InformationFormationPage = lazy(
	() => import('../modules/Formations/pages/InformationPage/InformationPage'),
);

const EnrollFormationPage = lazy(() => import('../modules/Formations/pages/EnrollPage/EnrollPage'));

const NotesFormationPage = lazy(() => import('../modules/Formations/pages/NotesPage/NotesPage'));

const FollowUpFormationPage = lazy(
	() => import('../modules/Formations/pages/FollowUpPage/FollowUpPage'),
);

const FilesFormationPage = lazy(() => import('../modules/Formations/pages/FilesPage/FilesPage'));

const BillsFormationPage = lazy(
	() => import('../modules/Formations/pages/BillsPage/BillsFormationPage'),
);

// ! Budget
const BudgetsPage = lazy(() => import('../modules/Budgets/pages/BudgetsPage/BudgetsPage'));
const BudgetDetailLayout = lazy(() => import('../modules/Budgets/layout/TabsNavigation.layout'));
const BudgetDetailPage = lazy(
	() => import('../modules/Budgets/pages/BudgetDetailPage/BudgetDetailPage'),
);
const BudgetCreatePage = lazy(() => import('../modules/Budgets/pages/CreateBudget/CreateBudget'));
const BudgetEditPage = lazy(() => import('../modules/Budgets/pages/BudgetEditPage/BudgetEditPage'));
const BudgetFollowUpPage = lazy(() => import('../modules/Budgets/pages/FollowUpPage/FollowUpPage'));

// ! Invoice
const InvoicePage = lazy(() => import('../modules/Invoices/pages/InvoicePage/InvoicePage'));
const InvoiceCreatePage = lazy(
	() => import('../modules/Invoices/pages/InvoiceCreatePage/InvoiceCreatePage'),
);
const InvoiceEditPage = lazy(
	() => import('../modules/Invoices/pages/InvoiceEditPage/InvoiceEditPage'),
);
const InvoiceDetailLayout = lazy(() => import('../modules/Invoices/layout/TabsNavigation.layout'));
const InvoiceDetailPage = lazy(
	() => import('../modules/Invoices/pages/InvoiceDetailPage/InvoiceDetailPage'),
);
const InvoiceFollowUpPage = lazy(
	() => import('../modules/Invoices/pages/InvoiceFollowUpPage/InvoiceFollowUpPage'),
);

// ! Bill
const BillPage = lazy(() => import('../modules/Bills/pages/BillsPage/BillsPage'));
const BillCreatePage = lazy(() => import('../modules/Bills/pages/BillCreatePage/BillCreatePage'));
const BillEditPage = lazy(() => import('../modules/Bills/pages/BillEditPage/BillEditPage'));
const BillDetailLayout = lazy(() => import('../modules/Bills/layout/TabsNavigation.layout'));
const BillDetailPage = lazy(() => import('../modules/Bills/pages/BillDetailPage/BillDetailPage'));
const BillFollowUpPage = lazy(
	() => import('../modules/Bills/pages/BillFollowUpPage/BillFollowUpPage'),
);

// ! BillsTrainer
const BillsTrainerPage = lazy(
	() => import('../modules/BillsTrainer/pages/BillsTrainerPage/BillsTrainerPage'),
);
const BillCreateTrainerPage = lazy(
	() => import('../modules/BillsTrainer/pages/BillCreateTrainerPage/BillCreateTrainerPage'),
);
const BillEditTrainerPage = lazy(
	() => import('../modules/BillsTrainer/pages/BillEditTrainerPage/BillEditTrainerPage'),
);
const BillDetailTrainerPage = lazy(
	() => import('../modules/BillsTrainer/pages/BillDetailTrainerPage/BillDetailTrainerPage'),
);
const BillFollowUpTrainerPage = lazy(
	() => import('../modules/BillsTrainer/pages/BillFollowUpTrainerPage/BillFollowUpTrainerPage'),
);
const TabsNavigationTrainerLayout = lazy(
	() => import('../modules/BillsTrainer/layout/TabsNavigationTrainer.layout'),
);

// Searches
const StudentsSearchPage = lazy(() => import('../modules/Searchs/pages/StudentsSearchPage'));

// ! Notebook
const CalendarPage = lazy(() => import('../modules/Calendar/pages/CalendarPage/CalendarPage'));

// ! Company Managment
const CompanyManagmentCompanyData = lazy(
	() => import('../modules/Societies/pages/DataSocieties/DataSocieties'),
);
const CompanyManagmentDetailSociety = lazy(
	() => import('../modules/Societies/pages/DetailSociety/DetailSociety'),
);

const CompanyManagmentBonusSociety = lazy(
	() => import('../modules/Societies/pages/BonusSociety/BonusSociety'),
);

const TabsNavigationSocietyLayout = lazy(
	() => import('../modules/Societies/layout/TabsNavigationSocietyLayout'),
);
const DetailSociety = lazy(() => import('../modules/Societies/pages/DetailSociety/DetailSociety'));
const FilesSociety = lazy(() => import('../modules/Societies/pages/FilesSociety/FilesSociety'));

// ! Foldables
const TabsFoldableLayout = lazy(
	() => import('../modules/Foldable/Layout/TabsNavigation/TabsNavigation.layout'),
);
const BrandsDefibrillatorsPage = lazy(
	() => import('../modules/Foldable/Pages/BrandsDefibrillatorsPage'),
);
const SectorPage = lazy(() => import('../modules/Foldable/Pages/SectorPage'));
const FamilyPage = lazy(() => import('../modules/Foldable/Pages/FamilyPage'));
const PaymentConditionPage = lazy(
	() => import('../modules/Foldable/Pages/PaymentConditionPage'),
);

// ! Count document
const TabsLegalDataLayout = lazy(() => import('../modules/LegalData/layout/TabsLegalDataLayout'));
const LegalDataCounterPage = lazy(() => import('../modules/LegalData/pages/CounterDocumentPage'));
const TabsNavigationLegalDataLayout = lazy(
	() => import('../modules/LegalData/layout/TabsNavigation/TabsNavigation.layout'),
);
const InvoicedItemPage = lazy(() => import('../modules/LegalData/pages/InvoicedItemPage'));
const BudgetItemPage = lazy(() => import('../modules/LegalData/pages/BudgetItemPage'));
const FormationItemPage = lazy(() => import('../modules/LegalData/pages/FormationItemPage'));
const DiplomaItemPage = lazy(() => import('../modules/LegalData/pages/DiplomaItemPage'));

// ! Google calendar
const GoogleCalendar = lazy(() => import('../modules/TechnicalData/pages/GoogleCalendar'));

// ! Server
const ServerPage = lazy(() => import('../modules/TechnicalData/pages/ConfigServer'));

const UserRoleLayout = lazy(
	() => import('../modules/UserPermission/layout/UserRoleLayout/UserRoleLayout'),
);
const SettingsLopdTextPage = lazy(() => import('../modules/LegalData/pages/LopdTextPage'));
const SettingsVatExemptionsPage = lazy(
	() => import('../modules/LegalData/pages/VatExemptionsPage'),
);
const SettingsUser = lazy(() => import('../modules/UserPermission/pages/UserPage'));
const SettingsRole = lazy(() => import('../modules/UserPermission/pages/RolePage'));
const SettingsAccessHistory = lazy(
	() => import('../modules/UserPermission/pages/AccessHistoryPage'),
);

// ! Account
const AccountPage = lazy(() => import('../modules/Account/pages/AccountPage/AccountPage'));

// ! Login
const LoginPage = lazy(() => import('../modules/Login/pages/LoginPage/LoginPage'));
const ProfilePage = lazy(() => import('../modules/Login/pages/ProfilePage/ProfilePage'));

// ! Portal Subcontrata
const LoginSubcontractPage = lazy(
	() => import('../modules/PortalSubcontract/pages/LoginSubcontractPage/LoginSubcontractPage'),
);
const PortalSubcontractLayout = lazy(
	() =>
		import('../modules/PortalSubcontract/layouts/PortalSubcontractLayout/PortalSubcontractLayout'),
);
const PortalSubcontractResumePage = lazy(
	() =>
		import('../modules/PortalSubcontract/pages/PortalSubcontractResumePage/PortalSubcontractResumePage'),
);
const PortalSubcontractClientsPage = lazy(
	() =>
		import('../modules/PortalSubcontract/pages/PortalSubcontractClientsPage/PortalSubcontractClientsPage'),
);
const PortalSubcontractFormationsPage = lazy(
	() =>
		import('../modules/PortalSubcontract/pages/PortalSubcontractFormationsPage/PortalSubcontractFormationsPage'),
);
const PortalSubcontractDefibrillatorsPage = lazy(
	() =>
		import('../modules/PortalSubcontract/pages/PortalSubcontractDefibrillatorsPage/PortalSubcontractDefibrillatorsPage'),
);
const PortalSubcontractClientDetailPage = lazy(
	() =>
		import('../modules/PortalSubcontract/pages/PortalSubcontractClientDetailPage/PortalSubcontractClientDetailPage'),
);
const PortalSubcontractFormationDetailPage = lazy(
	() =>
		import('../modules/PortalSubcontract/pages/PortalSubcontractFormationDetailPage/PortalSubcontractFormationDetailPage'),
);

// ! Portal Cliente
const LoginClientPage = lazy(
	() => import('../modules/PortalClient/pages/LoginClientPage/LoginClientPage'),
);
const PortalClientLayout = lazy(
	() => import('../modules/PortalClient/layouts/PortalClientLayout/PortalClientLayout'),
);
const PortalClientResumePage = lazy(
	() => import('../modules/PortalClient/pages/PortalClientResumePage/PortalClientResumePage'),
);
const PortalClientFormationsPage = lazy(
	() =>
		import('../modules/PortalClient/pages/PortalClientFormationsPage/PortalClientFormationsPage'),
);
const PortalClientFormationDetailPage = lazy(
	() =>
		import('../modules/PortalClient/pages/PortalClientFormationDetailPage/PortalClientFormationDetailPage'),
);
const PortalClientDefibrillatorsPage = lazy(
	() =>
		import('../modules/PortalClient/pages/PortalClientDefibrillatorsPage/PortalClientDefibrillatorsPage'),
);
const PortalTrainerLayout = lazy(
	() => import('../modules/PortalTrainer/layouts/PortalTrainerLayout/PortalTrainerLayout'),
);
const PortalTrainerCalendarPage = lazy(
	() =>
		import('../modules/PortalTrainer/pages/PortalTrainerCalendarPage/PortalTrainerCalendarPage'),
);
const PortalTrainerFormationDetailPage = lazy(
	() =>
		import('../modules/PortalTrainer/pages/PortalTrainerFormationDetailPage/PortalTrainerFormationDetailPage'),
);

// ! Resume & Dashboard
const ProductResumePage = lazy(() => import('../modules/ProductResume/pages/ProductResume'));
const FormationResumePage = lazy(() => import('../modules/FormationResume/pages/FormationResume'));
const ClientBillingResume = lazy(
	() => import('../modules/ClientBillingResume/pages/ClientBillingResume'),
);
const SupplierBillingResume = lazy(
	() => import('../modules/SupplierBillingResume/pages/SupplierBillingResume'),
);
const TrainerResume = lazy(() => import('../modules/TrainerResume/pages/TrainerResume'));
const BillingByCompanyPage = lazy(
	() => import('../modules/BillingByComponay/pages/BillingByCompanyPage'),
);

const protectRoute = (element: ReactNode, permission?: PermissionRequirement) =>
	permission ? (
		<PermissionGuard requiredPermissions={permission}>{element}</PermissionGuard>
	) : (
		element
	);

const protectSession = (element: ReactNode) => <SessionGuard>{element}</SessionGuard>;

/**
 * Cuelga el manejador de chunks caducados de cada ruta raíz. React Router
 * propaga el error al `errorElement` ancestro más cercano, así que sin esto
 * solo quedaba cubierta la rama que lo declaraba a mano.
 */
const withChunkErrorBoundary = (routes: RouteObject[]): RouteObject[] =>
	routes.map(route => ({ errorElement: <ChunkLoadErrorBoundary />, ...route }));

const appRoutes: RouteObject[] = [
	// Dashboard
	{
		path: '/',
		element: <Navigate to="/dashboard/product" replace />,
	},
	// Búsquedas
	{
		path: '/searches',
		element: <SidebarLayout />,
		children: [
			{ path: '', element: <Navigate to="/searches/students" replace /> },
			{
				path: 'students',
				element: protectRoute(<StudentsSearchPage />, 'search.students'),
			},
		],
	},
	{
		path: '/dashboard',
		element: <SidebarLayout />,
		children: [
			{ path: '', element: <Navigate to="/dashboard/product" replace /> },
			{ path: '*', element: <Navigate to="/dashboard/product" replace /> },
			{ path: 'product', element: protectRoute(<ProductResumePage />, 'dashboard.read') },
			{
				path: 'formation',
				element: protectRoute(<FormationResumePage />, 'dashboard.read'),
			},
			{
				path: 'client-billing',
				element: protectRoute(<ClientBillingResume />, 'dashboard.read'),
			},
			{
				path: 'supplier-billing',
				element: protectRoute(<SupplierBillingResume />, 'dashboard.read'),
			},
			{
				path: 'trainer-resume',
				element: protectRoute(<TrainerResume />, 'dashboard.read'),
			},
			{
				path: 'company-billing',
				element: protectRoute(<BillingByCompanyPage />, 'dashboard.read'),
			},
			{
				path: 'training-billing',
				element: protectRoute(<DashboardPage />, 'dashboard.read'),
			},
		],
	},
	// Clients and Suppliers
	{
		path: '/client-suppler',
		element: <SidebarLayout />,
		children: [
			{ path: '', element: <Navigate to="/client-suppler/client" replace /> },
			{ path: 'client', element: protectRoute(<ClientPage />, 'clients.read') },
			{
				path: 'client/:clientId',
				element: protectRoute(<TabsNavigationClientLayout />, 'clients.read'),
				children: [
					{
						path: '',
						element: <Navigate to="/client-suppler/client" replace />,
					},
					{
						path: 'information',
						element: protectRoute(<InformationClientPage />, 'clients.read'),
					},
					{
						path: 'formation',
						element: protectRoute(<FormationClientPage />, 'clients.formations'),
					},
					{
						path: 'defibrillator',
						element: protectRoute(
							<DefibrillatorClientPage />,
							'clients.defibrillators',
						),
					},
					{
						path: 'budget',
						element: protectRoute(<BudgetsClientPage />, 'clients.budgets'),
					},
					{
						path: 'invoice',
						element: protectRoute(<InvoicesClientPage />, 'clients.invoices'),
					},
					{
						path: 'follow-up',
						element: protectRoute(<FollowUpClientPage />, 'clients.follow-up'),
					},
				],
			},
			{ path: 'supplier', element: protectRoute(<SuppliersPage />, 'suppliers.read') },
			{
				path: 'supplier/:supplierId',
				element: protectRoute(<TabsNavigationSuppliersLayout />, 'suppliers.read'),
				children: [
					{
						path: '',
						element: <Navigate to="/client-suppler/supplier" replace />,
					},
					{
						path: 'information',
						element: protectRoute(<SuppliersInformationPage />, 'suppliers.read'),
					},
					{
						path: 'product',
						element: protectRoute(<ProductsSuppliersPage />, 'suppliers.products'),
					},
					{
						path: 'invoice',
						element: protectRoute(<InvoicesSuppliersPage />, 'suppliers.invoices'),
					},
					{
						path: 'follow-up',
						element: protectRoute(<FollowUpSuppliersPage />, 'suppliers.follow-up'),
					},
				],
			},
			{
				path: 'subcontract',
				element: protectRoute(<SubcontractorsPage />, 'subcontractors.read'),
			},
			{
				path: 'subcontract/:subcontractId',
				element: protectRoute(
					<TabsNavigationSubcontractorsLayout />,
					'subcontractors.read',
				),
				children: [
					{
						path: '',
						element: <Navigate to="/client-suppler/subcontract" replace />,
					},
					{
						path: 'information',
						element: protectRoute(
							<SubcontractorsInformationPage />,
							'subcontractors.read',
						),
					},
					{
						path: 'client',
						element: protectRoute(
							<SubcontractorsClientsPage />,
							'subcontractors.clients',
						),
					},
					{
						path: 'course',
						element: protectRoute(
							<SubcontractorsCoursesPage />,
							'subcontractors.formations',
						),
					},
					{
						path: 'invoice',
						element: protectRoute(
							<SubcontractorsInvoicesPage />,
							'subcontractors.invoices',
						),
					},
					{
						path: 'budget',
						element: protectRoute(
							<SubcontractorsBudgetsPage />,
							'subcontractors.invoices',
						),
					},
					{
						path: 'follow-up',
						element: protectRoute(
							<SubcontractorsFollowUpPage />,
							'subcontractors.follow-up',
						),
					},
				],
			},
		],
	},
	// Products and Services
	{
		path: '/products-and-services',
		element: <SidebarLayout />,
		children: [
			{
				element: <Navigate to="/products-and-services/defibrillator" replace />,
				path: '',
			},
			{
				path: 'defibrillator',
				element: protectRoute(<DefibrillatorPage />, 'defibrillators.read'),
			},
			{
				path: 'defibrillator/:defibrillatorId',
				element: protectRoute(
					<TabsNavigationDefibrillatorsLayout />,
					'defibrillators.read',
				),
				children: [
					{
						path: '',
						element: <Navigate to="/products-and-services/defibrillator" replace />,
					},
					{
						path: 'information',
						element: protectRoute(
							<InformationDefibrillatorPage />,
							'defibrillators.read',
						),
					},
					{
						path: 'review',
						element: protectRoute(
							<ReviewDefibrillatorsPage />,
							'defibrillators.reviews',
						),
					},
					{
						path: 'note',
						element: protectRoute(<NotesDefibrillatorsPage />, 'defibrillators.notes'),
					},
					{
						path: 'follow-up',
						element: protectRoute(
							<FollowUpDefibrillatorsPage />,
							'defibrillators.follow-up',
						),
					},
				],
			},
			{ path: 'product', element: protectRoute(<ProductsPage />, 'products.read') },
			{ path: 'kit', element: protectRoute(<KitsPage />, 'kits.read') },
			{
				path: 'kit/:kitId',
				element: protectRoute(<TabsNavigationKitsLayout />, 'kits.read'),
				children: [
					{
						path: '',
						element: <Navigate to="/products-and-services/kit" replace />,
					},
					{
						path: 'information',
						element: protectRoute(<InformationKitsPage />, 'kits.read'),
					},
					{ path: 'history', element: protectRoute(<HistoryPage />, 'kits.history') },
				],
			},
		],
	},
	// Formations
	{
		path: '/trainings-trainers',
		element: <SidebarLayout />,
		children: [
			{
				path: '',
				element: <Navigate to="/trainings-trainers/trainer" replace />,
			},
			{ path: 'trainer', element: protectRoute(<TrainersPage />, 'trainers.read') },
			{
				path: 'trainer/:trainerId',
				element: protectRoute(<TabsNavigationTrainersLayout />, 'trainers.read'),
				children: [
					{
						path: '',
						element: <Navigate to="/trainings-trainers/trainer" replace />,
					},
					{
						path: 'information',
						element: protectRoute(<InformationTrainersPage />, 'trainers.read'),
					},
					{
						path: 'course',
						element: protectRoute(<CoursesTrainersPage />, 'trainers.courses'),
					},
					{
						path: 'note',
						element: protectRoute(<NoteTrainersPage />, 'trainers.notes'),
					},
					{
						path: 'kit',
						element: protectRoute(<KitsTrainersPage />, 'trainers.kits'),
					},
					{
						path: 'province',
						element: protectRoute(<ProvincesTrainersPage />, 'trainers.provinces'),
					},
					{
						path: 'follow-up',
						element: protectRoute(<FollowUpTrainersPage />, 'trainers.follow-up'),
					},
					{
						path: 'file',
						element: protectRoute(<FilesTrainersPage />, 'trainers.files'),
					},
				],
			},
			{
				path: 'type',
				element: protectRoute(<FormationTypePage />, 'formation-types.read'),
			},
			{
				path: 'type/:typeId',
				children: [
					{
						path: '',
						element: <Navigate to="/trainings-trainers/type" replace />,
					},
					{
						path: 'community',
						element: protectRoute(<FormationTypeCommunity />, 'formation-types.read'),
					},
					{
						path: 'community/:detailId',
						element: protectRoute(<FormationTypeDetailsPage />, 'formation-types.read'),
					},
					{
						path: 'community/cardio/:community',
						element: protectRoute(<FormationTypeDetailsPage />, 'formation-types.read'),
					},
					{
						path: 'community/informame/:community',
						element: protectRoute(<FormationTypeDetailsPage />, 'formation-types.read'),
					},
					{
						path: 'simple-detail',
						element: protectRoute(<FormationTypeDetailsPage />, 'formation-types.read'),
					},
				],
			},
			{ path: 'formation', element: protectRoute(<FormationPage />, 'formations.read') },
			{
				path: 'formation/:formationId',
				element: protectRoute(<TabsNavigationFormationsLayout />, 'formations.read'),
				children: [
					{
						path: '',
						element: <Navigate to="/trainings-trainers/formation" replace />,
					},
					{
						path: 'information',
						element: protectRoute(<InformationFormationPage />, 'formations.read'),
					},
					{
						path: 'note',
						element: protectRoute(<NotesFormationPage />, 'formations.notes'),
					},
					{
						path: 'enroll',
						element: protectRoute(<EnrollFormationPage />, 'formations.enrolled'),
					},
					{
						path: 'bill',
						element: protectRoute(<BillsFormationPage />, 'formations.bills'),
					},
					{
						path: 'follow-up',
						element: protectRoute(<FollowUpFormationPage />, 'formations.follow-up'),
					},
					{
						path: 'file',
						element: protectRoute(<FilesFormationPage />, 'formations.files'),
					},
				],
			},
		],
	},
	// Accounting
	{
		path: '/accounting',
		element: <SidebarLayout />,
		children: [
			{ path: '', element: <Navigate to="/accounting/budget" replace /> },
			{ path: '*', element: <Navigate to="/accounting/budget" replace /> },
			{ path: 'budget', element: protectRoute(<BudgetsPage />, 'budgets.read') },
			{
				path: 'budget/create',
				element: protectRoute(<BudgetCreatePage />, 'budgets.read'),
			},
			{
				path: 'budget/:budgetId/edit',
				element: protectRoute(<BudgetEditPage />, 'budgets.read'),
			},
			{
				path: 'budget/:budgetId',
				element: protectRoute(<BudgetDetailLayout />, 'budgets.read'),
				children: [
					{ path: '', element: <Navigate to="/accounting/budget" replace /> },
					{ path: '*', element: <Navigate to="/accounting/budget" replace /> },
					{
						path: 'detail',
						element: protectRoute(<BudgetDetailPage />, 'budgets.read'),
					},
					{
						path: 'follow-up',
						element: protectRoute(<BudgetFollowUpPage />, 'budgets.follow-up'),
					},
				],
			},
			{ path: 'invoice', element: protectRoute(<InvoicePage />, 'bills.read') },
			{
				path: 'invoice/create',
				element: protectRoute(<InvoiceCreatePage />, 'bills.read'),
			},
			{
				path: 'invoice/:invoiceId/edit',
				element: protectRoute(<InvoiceEditPage />, 'bills.read'),
			},
			{
				path: 'invoice/:invoiceId',
				element: protectRoute(<InvoiceDetailLayout />, 'bills.read'),
				children: [
					{ path: '', element: <Navigate to="/accounting/invoice" replace /> },
					{ path: '*', element: <Navigate to="/accounting/invoice" replace /> },
					{
						path: 'detail',
						element: protectRoute(<InvoiceDetailPage />, 'bills.read'),
					},
					{
						path: 'follow-up',
						element: protectRoute(<InvoiceFollowUpPage />, 'bills.follow-up'),
					},
				],
			},
			{ path: 'bill', element: protectRoute(<BillPage />, 'bills.read') },
			{ path: 'bill/create', element: protectRoute(<BillCreatePage />, 'bills.read') },
			{
				path: 'bill/:billId/edit',
				element: protectRoute(<BillEditPage />, 'bills.read'),
			},
			{
				path: 'bill/:billId',
				element: protectRoute(<BillDetailLayout />, 'bills.read'),
				children: [
					{ path: '', element: <Navigate to="/accounting/bill" replace /> },
					{ path: '*', element: <Navigate to="/accounting/bill" replace /> },
					{ path: 'detail', element: protectRoute(<BillDetailPage />, 'bills.read') },
					{
						path: 'follow-up',
						element: protectRoute(<BillFollowUpPage />, 'bills.follow-up'),
					},
				],
			},
			{
				path: 'trainer-bill',
				element: protectRoute(<BillsTrainerPage />, 'bills-trainer.read'),
			},
			{
				path: 'trainer-bill/create',
				element: protectRoute(<BillCreateTrainerPage />, 'bills-trainer.read'),
			},
			{
				path: 'trainer-bill/:billTrainerId/edit',
				element: protectRoute(<BillEditTrainerPage />, 'bills-trainer.read'),
			},
			{
				path: 'trainer-bill/:billTrainerId',
				element: protectRoute(<TabsNavigationTrainerLayout />, 'bills-trainer.read'),
				children: [
					{ path: '', element: <Navigate to="/accounting/trainer-bill" replace /> },
					{ path: '*', element: <Navigate to="/accounting/trainer-bill" replace /> },
					{
						path: 'detail',
						element: protectRoute(<BillDetailTrainerPage />, 'bills-trainer.read'),
					},
					{
						path: 'follow-up',
						element: protectRoute(
							<BillFollowUpTrainerPage />,
							'bills-trainer.follow-up',
						),
					},
				],
			},
		],
	},
	// Notebook
	{
		path: '/Notebook',
		element: <SidebarLayout />,
		errorElement: <ChunkLoadErrorBoundary />,
		children: [{ path: '', element: protectRoute(<CalendarPage />, 'calendar.read') }],
	},
	// Settings
	{
		path: '/company-managment',
		element: <SidebarLayout />,
		children: [
			{ path: '', element: <Navigate to="/company-managment/company-data" replace /> },
			{ path: '*', element: <Navigate to="/company-managment/company-data" replace /> },
			{
				path: 'company-data',
				element: protectRoute(<CompanyManagmentCompanyData />, 'societies.read'),
			},
			{
				path: 'company-data/:societyId',
				element: protectRoute(<TabsNavigationSocietyLayout />, 'societies.read'),
				children: [
					{
						path: '',
						element: <Navigate to="details" replace />,
					},
					{
						path: 'details',
						element: protectRoute(<DetailSociety />, 'societies.read'),
					},
					{
						path: 'files',
						element: protectRoute(<FilesSociety />, 'societies.files'),
					},
				],
			},
			{
				path: 'company-data/:companyDataId',
				element: protectRoute(<CompanyManagmentDetailSociety />, 'societies.read'),
			},
			{
				path: 'bonus-company',
				element: protectRoute(<CompanyManagmentBonusSociety />, 'societies.bonus'),
			},
		],
	},

	{
		path: '/user-role',
		element: <SidebarLayout />,
		children: [
			{
				element: <UserRoleLayout />,
				children: [
					{ path: '', element: <Navigate to="/user-role/user" replace /> },
					{ path: '*', element: <Navigate to="/user-role/user" replace /> },
					{ path: 'user', element: protectRoute(<SettingsUser />, 'users.read') },
					{ path: 'role', element: protectRoute(<SettingsRole />, 'roles.read') },
					{
						path: 'access-history',
						element: protectRoute(<SettingsAccessHistory />, 'permissions.read'),
					},
				],
			},
		],
	},
	// General data
	{
		path: '/general-data',
		element: <SidebarLayout />,
		children: [
			{
				path: 'legal-data',
				children: [
					{
						element: protectRoute(<TabsNavigationLegalDataLayout />, 'legal-data.read'),
						children: [
							{
								path: '',
								element: <Navigate to="/general-data/legal-data/lopd" replace />,
							},
							{
								path: '*',
								element: <Navigate to="/general-data/legal-data/lopd" replace />,
							},
							{
								path: 'lopd',
								element: protectRoute(<SettingsLopdTextPage />, 'legal-data.read'),
							},
							{
								path: 'vat',
								element: protectRoute(
									<SettingsVatExemptionsPage />,
									'legal-data.vat',
								),
							},
							{
								path: 'counter-document',
								element: protectRoute(
									<LegalDataCounterPage />,
									'legal-data.counters',
								),
							},
						],
					},

					{
						path: 'counter-document/:counterId',
						element: protectRoute(<TabsLegalDataLayout />, 'legal-data.counters'),
						children: [
							{
								path: '',
								element: (
									<Navigate
										to="/general-data/legal-data/counter-document"
										replace
									/>
								),
							},
							{
								path: '*',
								element: (
									<Navigate
										to="/general-data/legal-data/counter-document"
										replace
									/>
								),
							},
							{
								path: 'invoiced',
								element: protectRoute(<InvoicedItemPage />, 'legal-data.counters'),
							},
							{
								path: 'budget',
								element: protectRoute(<BudgetItemPage />, 'legal-data.counters'),
							},
							{
								path: 'formation',
								element: protectRoute(<FormationItemPage />, 'legal-data.counters'),
							},
							{
								path: 'diploma',
								element: protectRoute(<DiplomaItemPage />, 'legal-data.counters'),
							},
						],
					},
				],
			},
			{
				path: '',
				element: <Navigate to="/general-data/legal-data/lopd" replace />,
			},
			{
				path: '*',
				element: <Navigate to="/general-data/legal-data/lopd" replace />,
			},
			{
				path: 'foldable',
				children: [
					{
						element: <TabsFoldableLayout />,
						children: [
							{
								path: '',
								element: (
									<Navigate
										to="/general-data/foldable/brand-defibrilator"
										replace
									/>
								),
							},
							{
								path: 'brand-defibrilator',
								element: protectRoute(
									<BrandsDefibrillatorsPage />,
									'foldable.brands',
								),
							},
							{
								path: 'sector',
								element: protectRoute(<SectorPage />, 'foldable.sectors'),
							},
							{
								path: 'family',
								element: protectRoute(<FamilyPage />, 'foldable.families'),
							},
							{
								path: 'payment-condition',
								element: protectRoute(
									<PaymentConditionPage />,
									'foldable.payment-conditions',
								),
							},
						],
					},
				],
			},
		],
	},
	// Technical data
	{
		path: '/technical-data',
		element: <SidebarLayout />,
		children: [
			{ path: '', element: <Navigate to="/technical-data/google-calendar" replace /> },
			{ path: '*', element: <Navigate to="/technical-data/google-calendar" replace /> },

			{
				path: 'google-calendar',
				element: protectRoute(<GoogleCalendar />, 'technical-data.google-calendar'),
			},
			{
				path: 'server',
				element: protectRoute(<ServerPage />, 'technical-data.server'),
			},
		],
	},

	// Account
	{
		path: '/account',
		element: <SidebarLayout />,
		children: [
			{ path: '', element: <Navigate to="/account/profile" replace /> },
			{ path: 'profile', element: protectSession(<AccountPage />) },
		],
	},
	// Login
	{
		path: '/login',
		element: <LoginPage />,
		children: [{ path: '', element: <Navigate to="/login" replace /> }],
	},
	// Login dinámico de portales (branding por subcontrata)
	{
		path: '/login/:slug',
		element: <LoginSubcontractPage />,
	},
	{
		path: '/login/:slug/cliente',
		element: <LoginClientPage />,
	},
	// Portal Subcontrata
	{
		path: '/portal/subcontrata',
		element: protectRoute(<PortalSubcontractLayout />, 'portal-subcontract.read'),
		children: [
			{ path: '', element: <Navigate to="/portal/subcontrata/resumen" replace /> },
			{ path: '*', element: <Navigate to="/portal/subcontrata/resumen" replace /> },
			{
				path: 'resumen',
				element: protectRoute(<PortalSubcontractResumePage />, 'portal-subcontract.read'),
			},
			{
				path: 'clientes',
				element: protectRoute(<PortalSubcontractClientsPage />, 'portal-subcontract.read'),
			},
			{
				path: 'clientes/:clientId',
				element: protectRoute(
					<PortalSubcontractClientDetailPage />,
					'portal-subcontract.read',
				),
			},
			{
				path: 'formaciones',
				element: protectRoute(
					<PortalSubcontractFormationsPage />,
					'portal-subcontract.read',
				),
			},
			{
				path: 'formaciones/:formationId',
				element: protectRoute(
					<PortalSubcontractFormationDetailPage />,
					'portal-subcontract.read',
				),
			},
			{
				path: 'desfibriladores',
				element: protectRoute(
					<PortalSubcontractDefibrillatorsPage />,
					'portal-subcontract.read',
				),
			},
		],
	},
	// Portal Cliente
	{
		path: '/portal/cliente',
		element: protectRoute(<PortalClientLayout />, 'portal-client.read'),
		children: [
			{ path: '', element: <Navigate to="/portal/cliente/resumen" replace /> },
			{ path: '*', element: <Navigate to="/portal/cliente/resumen" replace /> },
			{
				path: 'resumen',
				element: protectRoute(<PortalClientResumePage />, 'portal-client.read'),
			},
			{
				path: 'formaciones',
				element: protectRoute(<PortalClientFormationsPage />, 'portal-client.read'),
			},
			{
				path: 'formaciones/:formationId',
				element: protectRoute(<PortalClientFormationDetailPage />, 'portal-client.read'),
			},
			{
				path: 'desfibriladores',
				element: protectRoute(<PortalClientDefibrillatorsPage />, 'portal-client.read'),
			},
		],
	},
	// Portal Formador
	{
		path: '/portal/formador',
		element: protectRoute(<PortalTrainerLayout />, 'portal-trainer.read'),
		children: [
			{ path: '', element: <Navigate to="/portal/formador/calendario" replace /> },
			{ path: '*', element: <Navigate to="/portal/formador/calendario" replace /> },
			{
				path: 'calendario',
				element: protectRoute(<PortalTrainerCalendarPage />, 'portal-trainer.read'),
			},
			{
				path: 'formaciones/:formationId',
				element: protectRoute(<PortalTrainerFormationDetailPage />, 'portal-trainer.read'),
			},
		],
	},
	{
		path: '/profile',
		element: <SidebarLayout />,
		children: [{ path: '', element: protectSession(<ProfilePage />) }],
	},
	{
		path: '/not-found',
		element: <NotFoundPage />,
	},
	// Ruta comodín para manejar rutas no definidas
	{
		path: '*',
		element: <NotFoundPage />,
	},
];

export const router = createBrowserRouter(withChunkErrorBoundary(appRoutes), {
	future: {
		v7_normalizeFormMethod: true,
		v7_partialHydration: true,
		v7_relativeSplatPath: true,
		v7_fetcherPersist: true,
		v7_skipActionErrorRevalidation: true,
	},
});
